import { useState, useEffect, useCallback, useRef } from 'react';
import { formatRego, formatAUMobile } from '@/lib/formatters';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Save,
  AlertTriangle,
  Car,
  User,
  Phone,
  Key,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { melbourneDateKey, melbourneTimeKey, melbourneISO } from '@/lib/melbourneTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: 'service', label: 'Service', color: 'bg-blue-100 text-blue-700' },
  { value: 'rwc', label: 'RWC (Roadworthy)', color: 'bg-green-100 text-green-700' },
  { value: 'tyres', label: 'Tyres', color: 'bg-purple-100 text-purple-700' },
  { value: 'aircon', label: 'Aircon', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'brakes', label: 'Brakes', color: 'bg-orange-100 text-orange-700' },
  { value: 'suspension', label: 'Suspension', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'electrical', label: 'Electrical', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

export default function BookingEditModal({ 
  booking, 
  open, 
  onClose, 
  onUpdate,
  onCancel,
  getSiteParams 
}) {
  const [loading, setLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  // Tracks which form fields the SM hasn't yet typed into. The async
  // customer-doc GET that fires after the modal opens only overlays
  // values onto pristine fields — so typing into Suburb at t=20ms
  // doesn't get reverted at t=200ms when the customer fetch resolves.
  const pristineRef = useRef(new Set());
  
  // Form state - the FULL customer form so the booking modal can act as
  // a single source of truth for customer details (matching the JC modal
  // "Edit Customer Details" dialog). On save, customer fields are PUT to
  // /customers/{id} first (when booking has a customer_id) so changes
  // propagate to ALL future docs via the existing snapshot-sync logic,
  // then the booking-only fields are PUT to /bookings/{id}.
  const [formData, setFormData] = useState({
    // Customer (denormalised on booking + persisted to customer doc)
    customer_name: '',
    first_name: '',
    last_name: '',
    customer_phone: '',  // mobile in customer schema
    customer_landline: '',  // phone (landline) in customer schema
    customer_email: '',
    address_line1: '',
    address_line2: '',
    suburb: '',
    state: '',
    postcode: '',
    company: '',
    abn: '',
    // Vehicle (snapshot on booking; vehicle doc may not exist yet)
    vehicle_rego: '',
    vehicle_make: '',
    vehicle_model: '',
    // Booking-only
    scheduled_date: '',
    scheduled_time: '09:00',
    service_types: [],
    loan_car_requested: false,
    notes: ''
  });
  
  // Initialize form when booking changes. When the booking has a
  // customer_id, fetch the latest customer doc so the SM is editing the
  // CURRENT customer state (not just whatever was denormalised at
  // booking-creation time, which may be stale after a separate edit).
  useEffect(() => {
    if (!booking) return;
    const bookedDate = booking.booked_datetime
      ? melbourneDateKey(booking.booked_datetime)
      : '';
    const bookedTime = booking.booked_datetime
      ? melbourneTimeKey(booking.booked_datetime)
      : '09:00';

    // Seed from booking snapshot first — guarantees the form has SOMETHING
    // to show even if the customer fetch fails or the booking is
    // lead-mode (no customer_id).
    const seedData = {
      customer_name: booking.customer_name || '',
      first_name: booking.first_name || '',
      last_name: booking.last_name || '',
      customer_phone: booking.customer_phone || '',
      customer_landline: booking.customer_landline || '',
      customer_email: booking.customer_email || '',
      address_line1: booking.address_line1 || booking.customer_address || '',
      address_line2: booking.address_line2 || '',
      suburb: booking.suburb || '',
      state: booking.state || '',
      postcode: booking.postcode || '',
      company: booking.company || '',
      abn: booking.abn || '',
      vehicle_rego: booking.vehicle_rego || '',
      vehicle_make: booking.vehicle_make || '',
      vehicle_model: booking.vehicle_model || '',
      scheduled_date: bookedDate,
      scheduled_time: bookedTime,
      service_types: booking.service_types && Array.isArray(booking.service_types)
        ? booking.service_types
        : (booking.service_type ? [booking.service_type] : []),
      loan_car_requested: booking.loan_car_requested || false,
      notes: booking.notes || ''
    };
    setFormData(seedData);
    setCancelConfirm(false);
    // Pristine = fields untouched since the modal opened. We only allow
    // the async customer-doc fetch to overlay values onto PRISTINE
    // fields, so SM types are never silently reverted by the late
    // arriving GET /customers response. Reset every time the booking
    // prop changes.
    pristineRef.current = new Set(Object.keys(seedData));

    // Overlay the latest customer doc fields on top so the SM sees the
    // canonical address / company / ABN / landline that may have been
    // updated outside of this booking.
    const cid = booking.customer_id;
    if (!cid) return;
    let active = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/customers/${cid}`, { credentials: 'include' });
        if (!r.ok) return;
        const c = await r.json();
        if (!active) return;
        // Only patch fields that the SM hasn't already typed into.
        const overlay = {
          customer_name: c.display_name,
          first_name: c.first_name,
          last_name: c.last_name,
          customer_phone: c.mobile,
          customer_landline: c.phone,
          customer_email: c.email,
          address_line1: c.address_line1 || c.address,
          address_line2: c.address_line2,
          suburb: c.suburb,
          state: c.state,
          postcode: c.postcode,
          company: c.company,
          abn: c.abn,
        };
        setFormData(prev => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(overlay)) {
            // Only overlay when the field is still pristine AND the
            // customer doc actually has a value (don't blank out a
            // booking-snapshot value just because the customer doc
            // didn't carry it).
            if (pristineRef.current.has(k) && v) next[k] = v;
          }
          return next;
        });
      } catch (_e) { /* noop — booking form already has the snapshot */ }
    })();
    return () => { active = false; };
  }, [booking]);

  // Wrap handleInputChange so the first SM keystroke on a field marks it
  // as DIRTY — pristineRef is consulted by the customer-doc overlay above
  // so the SM's edits are never silently reverted by the async fetch.
  const handleInputChange = useCallback((key, value) => {
    pristineRef.current.delete(key);
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);
  
  const handleServiceTypeToggle = useCallback((type) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(type)
        ? prev.service_types.filter(t => t !== type)
        : [...prev.service_types, type]
    }));
  }, []);
  
  const handleSave = async () => {
    if (formData.service_types.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }
    
    if (!formData.scheduled_date) {
      toast.error('Please select a scheduled date');
      return;
    }
    
    setLoading(true);
    try {
      const siteParams = getSiteParams ? getSiteParams() : '';
      
      // ── Step 1: Persist customer changes to the canonical customer doc.
      // When the booking has a customer_id, send a PUT /customers/{id} so
      // every other doc (JCs, invoices, other bookings) picks up the
      // new name/phone/email/address via the existing snapshot-sync
      // logic in PUT /customers. Without this, SM edits to a booking's
      // customer fields stay on the booking row only — the customer doc
      // (and every JC/invoice copy of it) drifts.
      // Track customer PUT outcome so the final toast accurately
      // reflects whether the customer doc sync actually succeeded —
      // otherwise SM sees a green "Booking updated successfully" toast
      // even when the customer record stayed stale.
      let customerSyncFailed = false;
      let customerSyncError = null;
      if (booking.customer_id) {
        const displayName = (formData.customer_name || '').trim()
          || `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
        const customerPayload = {
          display_name: displayName || null,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          mobile: formData.customer_phone || null,
          phone: formData.customer_landline || null,
          email: formData.customer_email || null,
          address_line1: formData.address_line1 || null,
          address_line2: formData.address_line2 || null,
          suburb: formData.suburb || null,
          state: formData.state || null,
          postcode: formData.postcode || null,
          company: formData.company || null,
          abn: formData.abn || null,
        };
        try {
          const cRes = await fetch(`${BACKEND_URL}/api/customers/${booking.customer_id}${siteParams}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(customerPayload),
          });
          if (!cRes.ok) {
            const e = await cRes.json().catch(() => ({}));
            customerSyncFailed = true;
            customerSyncError = e.detail || `HTTP ${cRes.status}`;
          }
        } catch (e) {
          customerSyncFailed = true;
          customerSyncError = e?.message || 'Network error';
        }
      }

      const updateData = {
        customer_name: (formData.customer_name || '').trim()
          || `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email,
        vehicle_rego: formData.vehicle_rego.toUpperCase(),
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        // Compose proper UTC ISO from Melbourne wall-clock — guarantees the
        // booking date stays exactly on the day the SM picked, regardless of
        // server / browser timezone.
        booked_datetime: melbourneISO(formData.scheduled_date, formData.scheduled_time || '09:00'),
        service_types: formData.service_types,
        loan_car_requested: formData.loan_car_requested,
        notes: formData.notes
      };
      
      const response = await fetch(`${BACKEND_URL}/api/bookings/${booking.booking_id}${siteParams}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        if (customerSyncFailed) {
          // Customer doc PUT failed but the booking did save — let SM know
          // their customer-info edits did NOT propagate to the customer
          // record so they can retry from the JC "Edit Customer Details"
          // dialog (which always works).
          toast.warning('Booking saved, but customer details failed to sync', {
            description: customerSyncError || 'Open the JC and retry from Edit Customer Details.',
          });
        } else {
          toast.success('Booking updated successfully');
        }
        onUpdate && onUpdate();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update booking');
      }
    } catch (error) {
      console.error('Failed to update booking:', error);
      toast.error('Failed to update booking');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelBooking = async () => {
    if (!cancelConfirm) {
      setCancelConfirm(true);
      return;
    }
    
    setLoading(true);
    try {
      const siteParams = getSiteParams ? getSiteParams() : '';
      
      const response = await fetch(`${BACKEND_URL}/api/bookings/${booking.booking_id}${siteParams}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'cancelled' })
      });
      
      if (response.ok) {
        toast.success('Booking cancelled');
        onCancel && onCancel();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      toast.error('Failed to cancel booking');
    } finally {
      setLoading(false);
      setCancelConfirm(false);
    }
  };
  
  if (!booking) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto" data-testid="booking-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-blue-600 font-bold">{booking.booking_number}</span>
            <span className="text-slate-400">•</span>
            <span>Edit Booking</span>
          </DialogTitle>
        </DialogHeader>
        
        {/* Customer Details - Editable. Mirrors the full Edit Customer
            Details dialog used on JC modal so the booking modal can act
            as a single source of truth. Save propagates to both the
            booking AND the customer doc. */}
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Customer details — synced to customer record on save
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name" className="flex items-center gap-1">
                <User className="h-3 w-3" />
                Display Name
              </Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                placeholder="John Smith"
                className="mt-1"
                data-testid="booking-customer-display-name"
              />
            </div>
            <div>
              <Label htmlFor="customer_phone" className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Mobile
              </Label>
              <Input
                id="customer_phone"
                value={formData.customer_phone}
                onChange={(e) => handleInputChange('customer_phone', formatAUMobile(e.target.value))}
                placeholder="0400 123 456"
                className="mt-1"
                data-testid="booking-customer-mobile"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_first">First Name</Label>
              <Input
                id="customer_first"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className="mt-1"
                data-testid="booking-customer-first-name"
              />
            </div>
            <div>
              <Label htmlFor="customer_last">Last Name</Label>
              <Input
                id="customer_last"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className="mt-1"
                data-testid="booking-customer-last-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_landline">Landline / Phone</Label>
              <Input
                id="customer_landline"
                value={formData.customer_landline}
                onChange={(e) => handleInputChange('customer_landline', e.target.value)}
                placeholder="03 9876 5432"
                className="mt-1"
                data-testid="booking-customer-landline"
              />
            </div>
            <div>
              <Label htmlFor="customer_email" className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleInputChange('customer_email', e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
                data-testid="booking-customer-email"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="booking_addr1">Address Line 1</Label>
            <Input
              id="booking_addr1"
              value={formData.address_line1}
              onChange={(e) => handleInputChange('address_line1', e.target.value)}
              placeholder="1 Main St"
              className="mt-1"
              data-testid="booking-customer-addr1"
            />
          </div>
          <div>
            <Label htmlFor="booking_addr2">Address Line 2</Label>
            <Input
              id="booking_addr2"
              value={formData.address_line2}
              onChange={(e) => handleInputChange('address_line2', e.target.value)}
              placeholder="Unit 4 / PO Box 123"
              className="mt-1"
              data-testid="booking-customer-addr2"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="booking_suburb">Suburb</Label>
              <Input
                id="booking_suburb"
                value={formData.suburb}
                onChange={(e) => handleInputChange('suburb', e.target.value)}
                className="mt-1"
                data-testid="booking-customer-suburb"
              />
            </div>
            <div>
              <Label htmlFor="booking_state">State</Label>
              <Input
                id="booking_state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="VIC"
                className="mt-1"
                data-testid="booking-customer-state"
              />
            </div>
            <div>
              <Label htmlFor="booking_postcode">Postcode</Label>
              <Input
                id="booking_postcode"
                value={formData.postcode}
                onChange={(e) => handleInputChange('postcode', e.target.value)}
                placeholder="3000"
                className="mt-1"
                data-testid="booking-customer-postcode"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="booking_company">Company</Label>
              <Input
                id="booking_company"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="ACME Pty Ltd"
                className="mt-1"
                data-testid="booking-customer-company"
              />
            </div>
            <div>
              <Label htmlFor="booking_abn">ABN</Label>
              <Input
                id="booking_abn"
                value={formData.abn}
                onChange={(e) => handleInputChange('abn', e.target.value)}
                placeholder="12 345 678 901"
                className="mt-1"
                data-testid="booking-customer-abn"
              />
            </div>
          </div>
          
          {/* Vehicle Details - Editable */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="vehicle_rego" className="flex items-center gap-1">
                <Car className="h-3 w-3" />
                Rego
              </Label>
              <Input
                id="vehicle_rego"
                value={formData.vehicle_rego}
                onChange={(e) => handleInputChange('vehicle_rego', formatRego(e.target.value))}
                placeholder="ABC123"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="vehicle_make">Make</Label>
              <Input
                id="vehicle_make"
                value={formData.vehicle_make}
                onChange={(e) => handleInputChange('vehicle_make', e.target.value)}
                placeholder="Toyota"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="vehicle_model">Model</Label>
              <Input
                id="vehicle_model"
                value={formData.vehicle_model}
                onChange={(e) => handleInputChange('vehicle_model', e.target.value)}
                placeholder="Camry"
                className="mt-1"
              />
            </div>
          </div>
          
          {/* Scheduled Date + Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="scheduled_date" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Scheduled Date *
              </Label>
              <Input
                id="scheduled_date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => handleInputChange('scheduled_date', e.target.value)}
                className="mt-1"
                data-testid="booking-edit-date"
              />
              {!formData.scheduled_date && (
                <p className="text-xs text-red-500 mt-1">Please select a date</p>
              )}
            </div>
            <div>
              <Label htmlFor="scheduled_time">Time (Melbourne)</Label>
              <Input
                id="scheduled_time"
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => handleInputChange('scheduled_time', e.target.value)}
                className="mt-1"
                data-testid="booking-edit-time"
              />
            </div>
          </div>
          
          {/* Service Types */}
          <div>
            <Label className="mb-2 block">Service Types *</Label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_TYPES.map((type) => (
                <Badge
                  key={type.value}
                  className={`cursor-pointer transition-all ${
                    formData.service_types.includes(type.value)
                      ? type.color + ' ring-2 ring-offset-1 ring-blue-400'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                  onClick={() => handleServiceTypeToggle(type.value)}
                >
                  {type.label}
                </Badge>
              ))}
            </div>
            {formData.service_types.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please select at least one service type</p>
            )}
          </div>
          
          {/* Loan Car Request */}
          <div className={`p-4 rounded-lg border-2 ${formData.loan_car_requested ? 'bg-cyan-50 border-cyan-200' : 'bg-slate-50 border-slate-200'}`}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.loan_car_requested}
                onChange={(e) => handleInputChange('loan_car_requested', e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              <div className="flex items-center gap-2">
                <Key className={`h-5 w-5 ${formData.loan_car_requested ? 'text-cyan-600' : 'text-slate-400'}`} />
                <div>
                  <span className={`font-medium ${formData.loan_car_requested ? 'text-cyan-900' : 'text-slate-700'}`}>
                    Loan Car Required
                  </span>
                  <p className={`text-xs ${formData.loan_car_requested ? 'text-cyan-700' : 'text-slate-500'}`}>
                    Reserve a loan car for this booking
                  </p>
                </div>
              </div>
            </label>
          </div>
          
          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes..."
              className="mt-1"
            />
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2 mt-6">
          {/* Cancel Booking Button */}
          <div className="flex-1">
            {cancelConfirm ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleCancelBooking}
                  disabled={loading}
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Confirm Cancel
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCancelConfirm(false)}
                >
                  No
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleCancelBooking}
                disabled={loading}
              >
                Cancel Booking
              </Button>
            )}
          </div>
          
          {/* Save Button */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Close
            </Button>
            <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
